import servicesConfig, { CustomAxiosHeaders } from '../config/services'
import { requests } from '.'
import { cacheManager } from '../utils/CacheManager'
import { AxiosResponse } from 'axios'

export enum dailyMetricEnum {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS = 'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH = 'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS = 'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH = 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
  BUSINESS_CONVERSATIONS = 'BUSINESS_CONVERSATIONS',
  BUSINESS_DIRECTION_REQUESTS = 'BUSINESS_DIRECTION_REQUESTS',
  CALL_CLICKS = 'CALL_CLICKS',
  WEBSITE_CLICKS = 'WEBSITE_CLICKS',
  BUSINESS_BOOKINGS = 'BUSINESS_BOOKINGS',
  BUSINESS_FOOD_ORDERS = 'BUSINESS_FOOD_ORDERS',
}

export interface IGetDailyMetricsTimeSeries {
  timeSeries?: ITimeSeries
}

export interface ITimeSeries {
  datedValues?: IDatedValue[]
}

export interface IDatedValue {
  date?: IDateClass
  value?: string
}

export interface IDateClass {
  year?: number
  month?: number
  day?: number
}

export type GmbMetricsInsights = { [key in dailyMetricEnum]?: IGetDailyMetricsTimeSeries }

// Configure request setup
const serviceName = 'IntegrationsService'
const host = servicesConfig.Integrations as string
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

const marketplaceRequest = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, { serviceName: 'MarketplaceService', host: servicesConfig.MarketplaceService as string }, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

const IntegrationsService = {
  TikTok: {
    getURL: (locationId: string) => request().get(`/tiktok/oauth/url?locationId=${locationId}`).then(responseBody),
    create: (queryData: { [key: string]: any }) => request().post(`/tiktok/oauth/token`, queryData).then(responseBody),
    getAddAccounts: (id: string) => request().get(`/tiktok/${id}/accounts`).then(responseBody),
    upsert: (id: string, body: { [key: string]: any }) => request().put(`/tiktok/${id}`, body).then(responseBody),
    delete: (id: string) => request().delete(`/tiktok/${id}`).then(responseBody),
    getByLocation: (locationId: string) => request().get(`/tiktok?locationId=${locationId}`).then(responseBody),
    getForms: (id: string, skip?: number, limit?: number) => {
      const params: any = {}
      if (skip !== undefined) params.skip = skip
      if (limit) params.limit = limit
      return request().get(`/tiktok/${id}/forms`, { params }).then(responseBody)
    },
    subscribe: (id: string, data: { [key: string]: any }) =>
      request().put(`/tiktok/${id}/subscribe`, data).then(responseBody),
    upsertForm: (id: string, formId: string, data: { [key: string]: any }) =>
      request().put(`/tiktok/${id}/forms/${formId}`, data).then(responseBody),
  },

  GMB: {
    addReviewComment: (reviewId: string, comment: string) =>
      request().post(`/gmb/reviews/${reviewId}/comment`, { comment }).then(responseBody),
    checkBusinessScope: (locationId: string) =>
      request().get(`/gmb/oauth/scope/check?locationId=${locationId}`).then(responseBody),
    getAccountsLocations: (locationId: string, launchPad: boolean = false) => {
      const params = {
        locationId,
        launchPad,
      }
      return request().get(`/gmb/accounts`, { params }).then(responseBody)
    },
    getReportInsight: (locationId: string) => {
      const params = {
        locationId,
      }
      return request().get(`/gmb/report`, { params }).then(responseBody)
    },
    getMetricsInsight: (locationId: string): Promise<GmbMetricsInsights> => {
      const params = {
        locationId,
      }
      return request().get(`/gmb/metrics`, { params }).then(responseBody)
    },
    connect: (data: {
      locationId: string
      gmbLocationId: string
      gmbLocationName: string
      gmbPlacesId: string
      gmbLocationOnlyName: string
      isVerified: boolean
      enableCallTracking: boolean
      launchpad?: boolean
    }) => {
      return request()
        .post(`/gmb`, { ...data, single: true })
        .then(responseBody)
    },
    disconnect: (locationId: string) => {
      const data = {
        locationId,
      }
      return request().delete(`/gmb`, { data }).then(responseBody)
    },
    settings: (data: {
      locationId: string
      callTracking?: { enable: boolean }
      agent?: { enable: boolean }
      agentSetup?: {
        displayName: string
        welcomeMessage: string
        privacyPolicyUrl: string
      }
      placesId: string
      ignoreMessages?: boolean
    }) => {
      return request().post(`/gmb/settings`, data).then(responseBody)
    },
    updateProfilePicture: (locationId: string, file: any, placesId: string) => {
      const data = new FormData()
      data.append('files', file)

      let query = `locationId=${locationId}`
      if (placesId) {
        query += `&placesId=${placesId}`
      }

      return request().post(`/gmb/profilePicture?${query}`, data).then(responseBody)
    },
    getLinkedPages: (locationId: string) => {
      return cacheManager.getCachedData(
        `gmb-linkedPages`,
        () => request().get(`/gmb/linkedPages?locationId=${locationId}`).then(responseBody),
        {
          key: `gmb-linkedPages-${locationId}`,
        }
      )
    },
    disconnectAllPages: (locationId: string) => {
      const data = {
        locationId,
      }
      return request()
        .post(`/gmb/disconnect`, { ...data })
        .then(responseBody)
    },
  },

  Facebook: {
    syncFBMissingLeads: (locationId: string, pageId: string, syncLeadsTime: number) =>
      request()
        .post(`/facebook/${locationId}/pages/${pageId}/sync-leads`, { timeInDays: syncLeadsTime })
        .then(responseBody),
    getLinkedAdAccount: (locationId: string) => request().get(`/facebook/${locationId}/adAccount`).then(responseBody),
    getFBConnection: (locationId: string) => request().get(`/facebook/${locationId}/connection`).then(responseBody),
    getAllFbPages: (locationId: string) =>
      request().get(`/facebook/${locationId}/allPages?getAll=true`).then(responseBody),
    upsertSelectedFbPage: (locationId: string, data) =>
      request().post(`/facebook/${locationId}/pages`, data).then(responseBody),
    getFBConnectedPages: (locationId: string) =>
      request().get(`/facebook/${locationId}/pages?getAll=true`).then(responseBody),
  },

  MarketplaceIntegrations: {
    getCustomScripts: (companyId: string) => marketplaceRequest().get(`/custom-script/${companyId}`).then(responseBody),
  },

  Google: {
    getAllConnections: (locationId: string) =>
      request().get(`/google/connections?locationId=${locationId}`).then(responseBody),
  },
}

export default IntegrationsService
